import {
    Box,
    FormControl,
    Grid,
    IconButton,
    Input,
    InputAdornment,
    InputLabel,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import React, { ReactElement, useLayoutEffect } from 'react';
import { AppPrimaryButton } from 'shared/components/AppComponents';

import { useUploadPageState } from 'shared/hooks/UploadPage';

// import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ZoomIn from '@mui/icons-material/ZoomIn';
import ZoomOut from '@mui/icons-material/ZoomOut';
import { PDFDocument } from 'pdf-lib';
import FileSaver from 'file-saver';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import { VerticeReportFormObject } from './DocumentModal';
import { ManagePacketService } from 'services';
import { Utils } from 'shared/helpers';

const Footer: React.FC<SinglePacketDocumentViewerFooter> = React.forwardRef(
    (props, ref): ReactElement => {
        /**
         * Ref for the file input element.
         * @type {React.RefObject<HTMLInputElement>}
         */

        const theme = useTheme();

        const matchxs = useMediaQuery(theme.breakpoints.down('sm'));

        const [state] = useUploadPageState();
        const {
            onBack,
            onClose,
            documentPlaceholder,
            onZoomChanged,
            canCategorize,
            documentRef,
            isFullScreen,
            setFullScreen,
        } = props;

        const [isOpenDropdown] = React.useState<boolean>(false);

        const [zoomPx, setZoomPx] = React.useState<number>(10);
        const [documentName, setDocumentName] = React.useState<string | null>(
            null
        );
        const [packetId, setPacketId] = React.useState<
            string | null | undefined
        >(null);
        const [lengths, setLengths] = React.useState<number>(0);
        const [formObjects, setFormObject] = React.useState<
            VerticeReportFormObject[]
        >([]);
        const [, setTouch] = React.useState<boolean>(false);

        const handleTextFieldChange = React.useCallback(() => {
            setTouch(true);
        }, []);

        const [isDocumentSuccess, setDocumentSuccess] =
            React.useState<boolean>(false);
        const [isDocumentLoading, setDocumentLoading] =
            React.useState<boolean>(false);
        React.useLayoutEffect(() => {
            if (formObjects.length === lengths && lengths > 0) {
                setDocumentSuccess(true);
                const object = Array.from(formObjects)
                    .sort((a, b) => a.pageNumber - b.pageNumber)
                    .at(0);
                if (object) {
                    object?.element?.scrollIntoView({
                        block: 'center',
                        behavior: 'smooth',
                    });
                }
                formObjects.forEach((e) => {
                    e.element?.setAttribute('maxlength', '60');
                    e.element?.addEventListener(
                        'change',
                        handleTextFieldChange
                    );
                });
            }
            return () => {
                formObjects.forEach((e) => {
                    e.element?.removeEventListener(
                        'change',
                        handleTextFieldChange
                    );
                });
            };
        }, [formObjects]);
        React.useImperativeHandle(
            ref,
            () => ({
                /**
                 * check modal rework is already active with no selection dropdown opened
                 */
                isModalFooterActive: () =>
                    isOpenDropdown || state.uploadProgress,
                /**
                 * set default document data
                 * @param {RawDocument} _documentname - document data
                 * @param {RawDocument} _formObject - clientId from packet data
                 * @param {RawDocument} _packetId - packetId from actions in packet data
                 */
                setDefaultDocumentData: (_documentname, lengted, paketid) => {
                    setDocumentName(_documentname);
                    setFormObject([]);
                    setLengths(lengted);
                    setDocumentSuccess(false);

                    setPacketId(paketid);
                },
                setDocumentVerticesBinding: (pageIndex, _object) => {
                    setFormObject((prev) => {
                        const updateArray = prev.concat(_object);

                        return [...updateArray];
                    });
                },
                setDocumentSuccess: () => {
                    setDocumentSuccess(true);
                },
            }),
            [isOpenDropdown, state.uploadProgress]
        );

        const actionFullScreenForLeftViewer = React.useCallback(() => {
            if (typeof setFullScreen === 'function') {
                setFullScreen(!isFullScreen);
            }
        }, [isFullScreen, setFullScreen]);

        const onHandleZoomOut = React.useCallback(() => {
            if (zoomPx > 0) {
                const newzoomPx = zoomPx - 5;

                setZoomPx(newzoomPx);
            }
        }, [zoomPx]);
        const onHandleZoomIn = React.useCallback(() => {
            if (zoomPx < 100) {
                const newZoomPx = zoomPx + 5;

                setZoomPx(newZoomPx);
            }
        }, [zoomPx]);
        useLayoutEffect(() => {
            if (typeof onZoomChanged === 'function') {
                onZoomChanged(zoomPx);
            }
        }, [zoomPx]);

        const handleReportReviewed = React.useCallback(() => {
            if (packetId) {
                setDocumentLoading(true);
                ManagePacketService.reviewReport({
                    packetId,
                    verticesList: Array.from(formObjects).map((e) => ({
                        word: e.word,
                        x1: e.x1,
                        x2: e.x2,
                        x3: e.x3,
                        x4: e.x4,
                        y1: e.y1,
                        y2: e.y2,
                        y3: e.y3,
                        y4: e.y4,
                        pageNumber: e.pageNumber,
                        text: e.element?.value ?? '',
                    })),
                })
                    .then(() => {
                        if (typeof onClose === 'function') {
                            onClose();
                            setDocumentName('');
                            setFormObject([]);
                            setLengths(0);
                            setDocumentLoading(false);
                            setDocumentSuccess(false);
                            setPacketId(null);
                        }
                    })
                    .catch((error) => {
                        setDocumentLoading(false);
                        Utils.handleErrorResponse(error);
                    });
            }
        }, [formObjects, onClose, packetId]);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const actionDownloadDocumentInView = React.useCallback(async () => {
            if (documentRef && documentRef.current) {
                const { current: documentProxy } = documentRef;
                if (
                    documentProxy?.linkService &&
                    documentProxy?.linkService.current
                ) {
                    await documentProxy?.linkService.current.pdfDocument
                        ?.getData()
                        .then(async (res) => {
                            const pdfDoc = await PDFDocument.load(res);

                            const base64DataUri = await pdfDoc.saveAsBase64({
                                dataUri: true,
                            });

                            fetch(base64DataUri)
                                .then((resp) => resp.blob())
                                .then((blob) => {
                                    FileSaver.saveAs(blob, documentPlaceholder);
                                });
                        });
                }
            }
        }, [documentPlaceholder, documentRef]);

        return (
            <Box className="footer document-modal-footer">
                <Grid container spacing={2}>
                    <Grid
                        container
                        item
                        direction="row-reverse"
                        xs={12}
                        sm={12}
                        md={8}
                        spacing={1}
                        className="task-document-modal"
                    >
                        <Grid
                            item
                            xs={12}
                            sm={12}
                            md={canCategorize ? 12 : 12}
                            lg={canCategorize ? 12 : 12}
                            className="document-metadata"
                        >
                            <Stack
                                direction={
                                    !canCategorize && matchxs ? 'column' : 'row'
                                }
                                className="container-metadata"
                            >
                                {isFullScreen === false && (
                                    <Box className="filename-container">
                                        <Typography
                                            variant="h6"
                                            className="metadata-content"
                                        >
                                            File Name
                                        </Typography>
                                        <Typography
                                            data-testid="placeholder-filename"
                                            className="metadata-content"
                                        >
                                            {documentName}
                                        </Typography>
                                    </Box>
                                )}
                                <Box
                                    className={
                                        !matchxs
                                            ? 'zoom-container'
                                            : 'zoom-container inline'
                                    }
                                >
                                    <Stack
                                        direction="row"
                                        spacing={2}
                                        flexGrow={1}
                                        alignItems="flex-start"
                                        justifyContent="end"
                                    >
                                        <FormControl
                                            fullWidth
                                            variant="standard"
                                            className="aggregator-zoom"
                                        >
                                            <InputLabel htmlFor="standard-adornment-amount">
                                                Zoom
                                            </InputLabel>
                                            <Input
                                                inputProps={{
                                                    'data-testid':
                                                        'test-zoom-label',
                                                    role: 'spinbutton',
                                                    'aria-label': 'zoom',
                                                    'aria-invalid':
                                                        zoomPx < 0 ||
                                                        zoomPx > 100
                                                            ? 'true'
                                                            : 'false',
                                                    'aria-valuenow': zoomPx,
                                                    'aria-valuemax': 100,
                                                    'aria-valuemin': 0,
                                                }}
                                                disableUnderline
                                                value={`${zoomPx}%`}
                                                disabled
                                                id="standard-adornment-amount"
                                                startAdornment={
                                                    <InputAdornment position="start">
                                                        <IconButton
                                                            disabled={
                                                                !isDocumentSuccess
                                                            }
                                                            onClick={
                                                                onHandleZoomOut
                                                            }
                                                            //   onMouseDown={handleMouseDownPassword}
                                                            edge="end"
                                                            title="zoom out"
                                                        >
                                                            <ZoomOut />
                                                        </IconButton>
                                                    </InputAdornment>
                                                }
                                                endAdornment={
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            disabled={
                                                                !isDocumentSuccess
                                                            }
                                                            onClick={
                                                                onHandleZoomIn
                                                            }
                                                            //   onMouseDown={handleMouseDownPassword}
                                                            edge="end"
                                                            title="zoom in"
                                                        >
                                                            <ZoomIn />
                                                        </IconButton>
                                                    </InputAdornment>
                                                }
                                            />
                                        </FormControl>
                                        <IconButton
                                            className="tooltip-button"
                                            aria-label="fullscreen"
                                            disabled={!isDocumentSuccess}
                                            onClick={
                                                actionFullScreenForLeftViewer
                                            }
                                        >
                                            {isFullScreen ? (
                                                <CloseFullscreenIcon />
                                            ) : (
                                                <OpenInFullIcon />
                                            )}
                                        </IconButton>
                                        {/* <IconButton
                                            className="tooltip-button"
                                            aria-label="download"
                                            disabled={!isDocumentSuccess}
                                            onClick={
                                                actionDownloadDocumentInView
                                            }
                                        >
                                            <FileDownloadOutlinedIcon />
                                        </IconButton> */}
                                    </Stack>
                                </Box>
                            </Stack>
                        </Grid>
                    </Grid>
                    {isFullScreen === false && (
                        <Grid
                            item
                            xs={12}
                            sm={12}
                            md={4}
                            className="task-document-modal"
                        >
                            <Box className="saving-document-modal-not-expected">
                                <Grid
                                    className="saving-document-container"
                                    container
                                    spacing={2}
                                    direction={
                                        canCategorize ? 'row' : 'row-reverse'
                                    }
                                >
                                    <Grid
                                        item
                                        xs={!canCategorize ? 12 : 6}
                                        sm={!canCategorize ? 12 : 6}
                                        className="categorise-button"
                                    >
                                        <Stack
                                            direction={
                                                matchxs ? 'column' : 'row'
                                            }
                                            spacing={2}
                                        >
                                            <AppPrimaryButton
                                                title="Confirm & Proceed"
                                                className="btn-small save-button"
                                                isLoading={isDocumentLoading}
                                                loadingSize={22}
                                                disabled={
                                                    !isDocumentSuccess ||
                                                    isDocumentLoading
                                                }
                                                onClick={handleReportReviewed}
                                            />
                                            <AppPrimaryButton
                                                title="Close"
                                                className="btn-small back-button"
                                                onClick={onBack}
                                            />
                                        </Stack>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Grid>
                    )}
                </Grid>
            </Box>
        );
    }
);

export default Footer;
